import type { AccountGradingRecording } from "@repo/app-types/entities/teammatePerformanceSummary";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type RefObject,
} from "react";

export interface TranscriptAudioSync {
  isAutoScrollSuspended: boolean;
  resumeDirection: "down" | "up";
  resumeAutoScroll: () => void;
}

export function useTranscriptAudioSync(
  containerRef: RefObject<HTMLElement | null>,
  nativeAudio: HTMLAudioElement | null,
  recording: AccountGradingRecording | undefined,
): TranscriptAudioSync {
  const [isAutoScrollSuspended, setIsAutoScrollSuspended] = useState(false);
  const [resumeDirection, setResumeDirection] = useState<"down" | "up">("down");
  const resumeAutoScrollRef = useRef<() => void>(() => undefined);

  // Track whether the mouse is over the transcript regardless of recording (audio source) changes.
  const getIsMouseOver = useGetIsMouseOver(containerRef);

  // Sync scroll assuming the given recording fragments match the audio source loaded in the given audio element.
  const recordingId = recording?.id;
  const fragments = recording?.transcript?.fragments;
  useEffect(() => {
    setIsAutoScrollSuspended(false);

    const container = containerRef.current;
    if (!container || !recordingId || !fragments || !nativeAudio) {
      return;
    }

    const scrollContainer = findClosestScrollContainer(container);
    if (!scrollContainer) {
      return;
    }

    type PlayingFragment = {
      startTime: number;
      element: Element;
      isVisible: boolean;
      resumeDirection: "down" | "up";
    };
    const getPlayingFragment = (): PlayingFragment | undefined => {
      if (nativeAudio.paused) {
        return undefined;
      }

      // Round up on the nearest 10 ms so we can skip past discrepancies in audio frames and floating point precision
      const playTime = Math.ceil(nativeAudio.currentTime * 100) / 100;

      // Find the start time of the fragment that is currently playing
      const startTime = fragments.find((_fragment, index, allFragments) => {
        const nextFragment = allFragments.at(index + 1);

        return (
          nextFragment === undefined ||
          playTime < nextFragment.start_time / 1000
        );
      })?.start_time;

      if (startTime === undefined) {
        return undefined;
      }

      const element = container.querySelector(
        `[data-recording-id="${recordingId}"][data-timestamp="${startTime}"]`,
      );

      if (!element) {
        return undefined;
      }

      const clipRect = scrollContainer.getBoundingClientRect();
      const rect = element.getBoundingClientRect();
      const isVisible =
        rect.top < clipRect.bottom && clipRect.top < rect.bottom;
      // Recovery direction is only meaningful while the fragment is out of view
      const resumeDirection = rect.bottom <= clipRect.top ? "up" : "down";

      return {
        startTime,
        element,
        isVisible,
        resumeDirection,
      };
    };

    let scrollingRole: "user" | "system" | undefined;
    let scrollEndTimer: ReturnType<typeof setTimeout> | undefined;
    let isFollowingSuspended = false;

    // Current scroll sync state
    let playingFragment: PlayingFragment | undefined;
    let shouldForceScrollIntoView = false;

    // The resume trigger shows only while following is suspended during
    // playback and the playing fragment is out of view
    const syncSuspensionState = () => {
      if (
        isFollowingSuspended &&
        playingFragment &&
        !playingFragment.isVisible
      ) {
        setResumeDirection(playingFragment.resumeDirection);
        setIsAutoScrollSuspended(true);
      } else {
        setIsAutoScrollSuspended(false);
      }
    };

    const onScroll = () => {
      // A system scroll must never suspend following
      if (scrollingRole !== "system") {
        scrollingRole = "user";
        // Any manual scroll suspends following until play, seek, or resume
        isFollowingSuspended = true;
        playingFragment = getPlayingFragment();
        syncSuspensionState();
      }

      // Approximate `scrollend` with a throttle, since some browsers (like Safari) don't support it
      if (scrollEndTimer !== undefined) {
        clearTimeout(scrollEndTimer);
      }
      scrollEndTimer = setTimeout(() => {
        scrollingRole = undefined;
      }, 1000);
    };

    const onScrollEnd = () => {
      scrollingRole = undefined;

      // The `scrollend` (if supported) typically happens sooner, so clear the backup timer
      if (scrollEndTimer !== undefined) {
        clearTimeout(scrollEndTimer);
      }
      scrollEndTimer = undefined;
    };

    const onPlay = () => {
      isFollowingSuspended = false;
      playingFragment = getPlayingFragment();

      if (playingFragment) {
        playingFragment = {
          ...playingFragment,
          // When we start playing, mark the fragment as already visible to scroll it into view if necessary and sync the scroll on future time updates.
          isVisible: true,
        };

        // If the mouse was outside the transcript, force scroll the fragment to the center even if already visible. (If the mouse is on the transcript, we don't want to do this to avoid moving the click target.)
        shouldForceScrollIntoView = !getIsMouseOver();
      }
      syncSuspensionState();
    };

    const onSeeking = () => {
      // Treat seeks/jumps the same as starting playback.
      onPlay();
    };

    const onPause = () => {
      // Clear the state to stop the scroll sync; recovery is only offered during playback
      playingFragment = undefined;
      shouldForceScrollIntoView = false;
      syncSuspensionState();
    };

    const onTimeUpdate = () => {
      // Capture the previous state
      const prevPlayingFragment = playingFragment;
      const prevShouldForceScrollIntoView = shouldForceScrollIntoView;

      // Update the current state
      playingFragment = getPlayingFragment();
      shouldForceScrollIntoView = false;

      // If the active fragment was (or should've been) visible and it isn't visible now, sync the scroll unless the user is scrolling (because that would be annoying).
      if (
        // The playing fragment was previously visible
        prevPlayingFragment?.isVisible &&
        // Following was not suspended by a manual scroll
        !isFollowingSuspended &&
        // We still have a playing fragment
        playingFragment &&
        // The playing fragment is not currently visible, or the fragment changed, or the onPlay/onSeeking handler forced a scroll
        (!playingFragment.isVisible ||
          playingFragment.startTime !== prevPlayingFragment.startTime ||
          prevShouldForceScrollIntoView) &&
        // The user is not currently scrolling
        scrollingRole !== "user"
      ) {
        playingFragment = {
          ...playingFragment,
          // Optimistically mark it as visible if it isn't already, so we continue to sync the scroll
          isVisible: true,
        };
        // Mark this as a programmatic scroll
        scrollingRole = "system";
        // Center the fragment
        playingFragment.element.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }

      syncSuspensionState();
    };

    resumeAutoScrollRef.current = () => {
      const currentPlayingFragment = getPlayingFragment();

      if (currentPlayingFragment) {
        // Optimistically restore the existing follow behavior before scrolling,
        // so future time updates continue tracking the active fragment.
        playingFragment = {
          ...currentPlayingFragment,
          isVisible: true,
        };
        shouldForceScrollIntoView = false;
        scrollingRole = "system";
        isFollowingSuspended = false;
        currentPlayingFragment.element.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      } else {
        playingFragment = undefined;
      }
      syncSuspensionState();
    };

    scrollContainer.addEventListener("scroll", onScroll);
    scrollContainer.addEventListener("scrollend", onScrollEnd);

    nativeAudio.addEventListener("play", onPlay);
    nativeAudio.addEventListener("seeking", onSeeking);
    nativeAudio.addEventListener("pause", onPause);
    nativeAudio.addEventListener("timeupdate", onTimeUpdate);

    return () => {
      scrollContainer.removeEventListener("scroll", onScroll);
      scrollContainer.removeEventListener("scrollend", onScrollEnd);

      nativeAudio.removeEventListener("play", onPlay);
      nativeAudio.removeEventListener("seeking", onSeeking);
      nativeAudio.removeEventListener("pause", onPause);
      nativeAudio.removeEventListener("timeupdate", onTimeUpdate);

      resumeAutoScrollRef.current = () => undefined;

      if (scrollEndTimer !== undefined) {
        clearTimeout(scrollEndTimer);
      }
    };
  }, [containerRef, recordingId, fragments, nativeAudio, getIsMouseOver]);

  const resumeAutoScroll = useCallback(() => {
    resumeAutoScrollRef.current();
  }, []);

  return {
    isAutoScrollSuspended,
    resumeDirection,
    resumeAutoScroll,
  };
}

function useGetIsMouseOver(containerRef: RefObject<HTMLElement | null>) {
  const isMouseOverRef = useRef(false);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    const handleMouseOver = () => {
      isMouseOverRef.current = true;
    };

    const handleMouseLeave = () => {
      isMouseOverRef.current = false;
    };

    container.addEventListener("mouseover", handleMouseOver);
    container.addEventListener("mouseleave", handleMouseLeave);

    return () => {
      container.removeEventListener("mouseover", handleMouseOver);
      container.removeEventListener("mouseleave", handleMouseLeave);
    };
  }, [containerRef]);

  return useCallback(() => isMouseOverRef.current, []);
}

function findClosestScrollContainer(element: HTMLElement): HTMLElement | null {
  let ancestor: HTMLElement | null = element.parentElement;

  while (ancestor && ancestor.scrollHeight <= ancestor.clientHeight) {
    ancestor = ancestor.parentElement;
  }

  return ancestor;
}
